import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Sidebar from "../Dashboard/Sidebar";
import "../Dashboard/Dashboard.css";
import "./AssignmentSubmission.css";
import { Calendar, CloudUpload, FileText, ArrowRight, Link as LinkIcon, Download, ExternalLink } from "lucide-react";
import SubmissionModal from "./SubmissionModal";
import { getActiveTaskId, getActiveTrackId, setActiveTaskId, setActiveTrackId } from "../../utils/session";
import { profileApi, submissionsApi, tracksApi } from "../../api/client";
import { getApiErrorMessage } from "../../api/http";
import { resolveMediaUrl } from "../../utils/media";
import MarkdownPreview from "../Common/MarkdownPreview";

const LINK_BASED_TYPES = new Set(["drive_link", "github_link"]);
const SUBMISSION_TYPE_LABELS = {
  file: "File Upload",
  drive_link: "Drive Link",
  github_link: "GitHub Link",
  file_or_link: "File or Link",
};

const deriveFileNameFromUrl = (url) => {
  if (!url) return "";
  try {
    const noQuery = url.split("?")[0];
    return decodeURIComponent(noQuery.split("/").pop() || "");
  } catch {
    return "";
  }
};

const deriveDomainFromUrl = (url) => {
  if (!url) return "";
  try {
    const parsed = new URL(url);
    return parsed.hostname.replace(/^www\./i, "");
  } catch {
    return "";
  }
};

const deriveExtensionLabel = (url) => {
  const fileName = deriveFileNameFromUrl(url);
  const ext = fileName.includes(".") ? fileName.split(".").pop() : "";
  const normalized = `${ext || ""}`.trim().toUpperCase();
  if (!normalized) return "FILE";
  return normalized.slice(0, 5);
};

const getTaskSubmissionOptionLabel = (task, submission) => {
  const baseTitle = task?.title || "Task";
  if (!submission) return `${baseTitle} - Not submitted`;

  if (submission?.status === "graded") {
    const score = Number(submission?.score || 0) + Number(submission?.bonusPoints || 0);
    return `${baseTitle} - Graded (${score}/${task?.points || 0})`;
  }

  if (task?.allowResubmission) {
    return `${baseTitle} - Submitted (can resubmit)`;
  }

  return `${baseTitle} - Submitted (final)`;
};

const AssignmentSubmission = () => {
  const [searchParams] = useSearchParams();
  const [showModal, setShowModal] = useState(false);
  const [lastSubmissionResult, setLastSubmissionResult] = useState(null);
  const [accessibleTracks, setAccessibleTracks] = useState([]);
  const [selectedTrackId, setSelectedTrackId] = useState("");
  const [tasks, setTasks] = useState([]);
  const [selectedTaskId, setSelectedTaskId] = useState("");
  const [mySubmission, setMySubmission] = useState(null);
  const [submissionByTaskId, setSubmissionByTaskId] = useState({});
  const [link, setLink] = useState("");
  const [file, setFile] = useState(null);
  const [comments, setComments] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    const loadTracks = async () => {
      setLoading(true);
      setError("");
      try {
        const { data } = await profileApi.me();
        const tracks = data?.profile?.tracks || [];
        setAccessibleTracks(tracks);

        if (!tracks.length) {
          setSelectedTrackId("");
          setTasks([]);
          setSubmissionByTaskId({});
          setError("You are not enrolled in any approved track yet.");
          return;
        }

        const queryTrackId = searchParams.get("trackId");
        const savedTrackId = getActiveTrackId();
        const selected =
          tracks.find((track) => track._id === queryTrackId) ||
          tracks.find((track) => track._id === savedTrackId) ||
          tracks[0];

        setSelectedTrackId(selected._id);
        setActiveTrackId(selected._id);
      } catch (err) {
        setError(getApiErrorMessage(err, "Failed to load your tracks"));
      } finally {
        setLoading(false);
      }
    };

    loadTracks();
  }, [searchParams]);

  useEffect(() => {
    const loadTasks = async () => {
      if (!selectedTrackId) return;
      setLoading(true);
      setError("");
      try {
        const { data } = await tracksApi.tasks(selectedTrackId);
        const list = data?.tasks || [];
        setTasks(list);
        const pairs = await Promise.all(
          list.map(async (task) => {
            try {
              const submissionRes = await submissionsApi.mySubmission(task._id);
              return [task._id, submissionRes.data?.submission || null];
            } catch {
              return [task._id, null];
            }
          })
        );
        const submissionsMap = Object.fromEntries(pairs);
        setSubmissionByTaskId(submissionsMap);

        const queryTaskId = searchParams.get("taskId");
        const savedTaskId = getActiveTaskId();
        const selectedTask =
          list.find((task) => task._id === queryTaskId) ||
          list.find((task) => task._id === savedTaskId) ||
          list[0] ||
          null;

        setSelectedTaskId(selectedTask?._id || "");
        if (selectedTask?._id) {
          setActiveTaskId(selectedTask._id);
        }
      } catch (err) {
        setTasks([]);
        setSubmissionByTaskId({});
        setSelectedTaskId("");
        setError(getApiErrorMessage(err, "Failed to load tasks for this track"));
      } finally {
        setLoading(false);
      }
    };

    loadTasks();
  }, [searchParams, selectedTrackId]);

  useEffect(() => {
    const loadMySubmission = async () => {
      if (!selectedTaskId) return;
      try {
        const { data } = await submissionsApi.mySubmission(selectedTaskId);
        const submission = data?.submission || null;
        setMySubmission(submission);
        setSubmissionByTaskId((prev) => ({ ...prev, [selectedTaskId]: submission }));
      } catch {
        setMySubmission(null);
        setSubmissionByTaskId((prev) => ({ ...prev, [selectedTaskId]: null }));
      }
    };

    loadMySubmission();
  }, [selectedTaskId]);

  useEffect(() => {
    setFile(null);
    const currentSubmission = submissionByTaskId[selectedTaskId] || mySubmission || null;
    setLink(currentSubmission?.link || "");
    setComments(currentSubmission?.comment || "");
  }, [selectedTaskId, submissionByTaskId, mySubmission]);

  const selectedTask = useMemo(() => {
    return tasks.find((task) => task._id === selectedTaskId) || null;
  }, [selectedTaskId, tasks]);
  const selectedSubmission = useMemo(
    () => submissionByTaskId[selectedTaskId] || mySubmission || null,
    [mySubmission, selectedTaskId, submissionByTaskId]
  );
  const selectedTaskIsSubmitted = Boolean(selectedSubmission);
  const selectedTaskCanResubmit = !selectedTaskIsSubmitted || Boolean(selectedTask?.allowResubmission);
  const selectedTaskIsLocked = selectedTaskIsSubmitted && !selectedTaskCanResubmit;
  const selectedTaskIsGraded = selectedSubmission?.status === "graded";
  const selectedTaskScore = Number(selectedSubmission?.score || 0) + Number(selectedSubmission?.bonusPoints || 0);
  const selectedTaskFeedback = `${selectedSubmission?.feedback || ""}`.trim();
  const selectedSubmissionFileUrl = useMemo(
    () => resolveMediaUrl(selectedSubmission?.fileUrl),
    [selectedSubmission?.fileUrl]
  );
  const selectedSubmissionLink = useMemo(
    () => resolveMediaUrl(selectedSubmission?.link),
    [selectedSubmission?.link]
  );
  const selectedSubmissionFileName =
    selectedSubmission?.fileName ||
    deriveFileNameFromUrl(selectedSubmissionFileUrl) ||
    "Uploaded file";
  const selectedSubmissionComment = `${selectedSubmission?.comment || ""}`.trim();

  const taskResources = useMemo(() => {
    if (!selectedTask) return [];

    const resourceLinks = Array.isArray(selectedTask.resourceLinks) ? selectedTask.resourceLinks : [];
    const resourceAttachments = Array.isArray(selectedTask.resourceAttachments) ? selectedTask.resourceAttachments : [];

    const linkItems = resourceLinks
      .map((rawUrl, idx) => `${rawUrl || ""}`.trim())
      .filter(Boolean)
      .map((url, idx) => ({
        key: `link-${idx}-${url}`,
        type: "link",
        url,
        ext: "URL",
        name: deriveDomainFromUrl(url) || `Resource Link ${idx + 1}`,
        subtitle: "Task resource link",
      }));

    const fileItems = resourceAttachments
      .map((rawUrl) => `${rawUrl || ""}`.trim())
      .filter(Boolean)
      .map((url, idx) => ({
        key: `file-${idx}-${url}`,
        type: "file",
        url,
        ext: deriveExtensionLabel(url),
        name: deriveFileNameFromUrl(url) || `Attachment ${idx + 1}`,
        subtitle: "Task resource file",
      }));

    return [...fileItems, ...linkItems];
  }, [selectedTask]);

  const submissionModeLabel = SUBMISSION_TYPE_LABELS[selectedTask?.submissionType] || "-";

  const isFileOnly = selectedTask?.submissionType === "file";
  const isLinkOnly = LINK_BASED_TYPES.has(selectedTask?.submissionType);
  const isFileOrLink = selectedTask?.submissionType === "file_or_link";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedTask) {
      setError("Please select a task.");
      return;
    }
    if (selectedTaskIsLocked) {
      setError("You already submitted this task and resubmission is not allowed.");
      return;
    }

    setError("");
    setMessage("");
    setLoading(true);

    try {
      const formData = new FormData();
      const normalizedLink = link.trim();
      const existingFileUrl = selectedSubmission?.fileUrl || "";
      const hasFileForSubmit = Boolean(file || existingFileUrl);
      const hasLinkForSubmit = Boolean(normalizedLink);

      if (isFileOnly && !hasFileForSubmit) {
        throw new Error("Please upload a file for this task.");
      }
      if (isLinkOnly && !hasLinkForSubmit) {
        throw new Error("Please enter a submission link.");
      }
      if (isFileOrLink && !hasFileForSubmit && !hasLinkForSubmit) {
        throw new Error("Please upload a file or provide a link.");
      }

      if (file) {
        formData.append("file", file);
      }
      if (isLinkOnly || isFileOrLink) {
        formData.append("link", normalizedLink);
      }
      formData.append("comment", comments.trim());

      const submittedFileName = file?.name || "";
      const { data } = await submissionsApi.submitTask(selectedTask._id, formData);
      const submission = data?.submission || null;
      const submittedVia = file
        ? "file"
        : normalizedLink
          ? "link"
          : submission?.fileUrl
            ? "file"
            : submission?.link
              ? "link"
              : "unknown";

      const resolvedFileName = submittedVia === "file"
        ? (submittedFileName || submission?.fileName || deriveFileNameFromUrl(submission?.fileUrl))
        : "";
      const resolvedLink = submittedVia === "link" ? (normalizedLink || submission?.link || "") : "";

      setLastSubmissionResult({
        fileName: resolvedFileName,
        fileUrl: submittedVia === "file" ? submission?.fileUrl || null : null,
        link: resolvedLink,
        submittedAt: submission?.submittedAt || new Date().toISOString(),
        allowResubmission: Boolean(selectedTask?.allowResubmission),
        deadline: selectedTask?.deadline || null,
        submittedVia,
      });

      setMessage("Submission sent successfully.");
      setShowModal(true);
      setFile(null);
      setLink("");
      setComments("");
      setMySubmission(submission);
      setSubmissionByTaskId((prev) => ({ ...prev, [selectedTask._id]: submission }));
    } catch (err) {
      setError(getApiErrorMessage(err, err.message || "Failed to submit assignment"));
    } finally {
      setLoading(false);
    }
  };

  const dueDateText = selectedTask?.deadline ? new Date(selectedTask.deadline).toLocaleString() : "No due date";

  return (
    <div className="dashboard-container">
      <div className="dashboard-sidebar-wrapper">
        <Sidebar />
      </div>
      <div className="dashboard-main-wrapper assignment-wrapper">
        <nav className="breadcrumb-nav">
          <span>Track</span>
          <span className="separator">/</span>
          <span>Tasks</span>
          <span className="separator">/</span>
          <span className="current">Submit Assignment</span>
        </nav>

        <div className="mb-3">
          <label className="form-label fw-bold">Select Track</label>
          <select className="form-select mb-3" value={selectedTrackId} onChange={(e) => {
            setSelectedTrackId(e.target.value);
            setActiveTrackId(e.target.value);
          }}>
            {accessibleTracks.map((track) => (
              <option key={track._id} value={track._id}>
                {track.name}
              </option>
            ))}
          </select>

          <label className="form-label fw-bold">Select Task</label>
          <select className="form-select" value={selectedTaskId} onChange={(e) => {
            setSelectedTaskId(e.target.value);
            setActiveTaskId(e.target.value);
          }}>
            {tasks.map((task) => (
              <option key={task._id} value={task._id}>
                {getTaskSubmissionOptionLabel(task, submissionByTaskId[task._id])}
              </option>
            ))}
          </select>
          {selectedTask ? (
            <div className={`task-selection-status${selectedTaskIsLocked ? " is-locked" : ""}${selectedTaskIsGraded ? " is-graded" : ""}`}>
              {selectedTaskIsSubmitted ? (
                <>
                  <strong>
                    {selectedTaskIsGraded
                      ? `Graded: ${selectedTaskScore}/${selectedTask?.points || 0}`
                      : "You already submitted this task."}
                  </strong>
                  <span>
                    {selectedTaskCanResubmit
                      ? "You can submit again. Your latest submission will be considered."
                      : "Resubmission is not allowed for this task."}
                  </span>
                  {selectedTaskFeedback ? (
                    <small>Feedback: {selectedTaskFeedback}</small>
                  ) : null}
                </>
              ) : (
                <>
                  <strong>Not submitted yet.</strong>
                  <span>Submit your task before the deadline.</span>
                </>
              )}
            </div>
          ) : null}
        </div>

        <div className="metrics-grid">
          <div className="metric-card due-date-card">
            <div className="card-bg-accent orange-accent"></div>
            <div className="metric-header">DUE DATE</div>
            <div className="metric-main">
              <Calendar size={20} className="icon-orange" />
              <div>
                <h3 className="metric-title">{dueDateText}</h3>
                <p className="metric-subtitle">Task deadline</p>
              </div>
            </div>
            <div className="metric-footer text-orange fw-bold">Keep enough time before deadline</div>
          </div>

          <div className="metric-card grading-card">
            <div className="card-bg-accent blue-accent"></div>
            <div className="metric-header">GRADING</div>
            <div className="metric-main align-items-baseline gap-2">
              <h3 className="metric-title display-points">{selectedTask?.points || 0}</h3>
              <p className="metric-subtitle">Points possible</p>
            </div>
            <div className="metric-footer grading-stats">
              <div className="stat-row">
                <span>Difficulty</span>
                <span className="fw-bold text-capitalize">{selectedTask?.difficulty || "-"}</span>
              </div>
              <div className="stat-row">
                <span>Submission Type</span>
                <span className="fw-bold">{selectedTask?.submissionType || "-"}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="assignment-brief paper-card">
          <div className="article-header">
            <h1 className="article-title">{selectedTask?.title || "Task Details"}</h1>
            <span className="status-badge pending-badge">
              <span className="dot"></span> {selectedSubmission ? selectedSubmission.status : "Pending Submission"}
            </span>
          </div>

          <MarkdownPreview
            className="article-intro text-muted"
            content={selectedTask?.description}
            emptyText="No detailed description for this task."
          />

          <div className="resources-section">
            <h5 className="section-subtitle">
              <FileText size={16} className="me-2 text-muted" />
              RESOURCES
            </h5>
            <div className="resources-grid">
              <div className="resource-card info-card">
                <div className="res-icon-box pdf-box">
                  <span className="res-ext">INFO</span>
                </div>
                <div className="res-info">
                  <span className="res-name fw-bold">Submission Mode</span>
                  <span className="res-size text-muted">{submissionModeLabel}</span>
                  <small className="res-note text-muted">This is a task rule, not a downloadable file.</small>
                </div>
              </div>
              {taskResources.map((resource) => (
                <div key={resource.key} className="resource-card">
                  <div className={`res-icon-box ${resource.type === "file" ? "file-box" : "link-box"}`}>
                    <span className="res-ext">{resource.ext}</span>
                  </div>
                  <div className="res-info">
                    <span className="res-name fw-bold">{resource.name}</span>
                    <span className="res-size text-muted">{resource.subtitle}</span>
                    <small className="res-note text-muted">{resource.url}</small>
                  </div>
                  <a
                    className="dl-btn resource-action-btn"
                    href={resource.url}
                    target="_blank"
                    rel="noreferrer"
                    download={resource.type === "file" ? true : undefined}
                    aria-label={resource.type === "file" ? "Download resource file" : "Open resource link"}
                    title={resource.type === "file" ? "Download file" : "Open link"}
                  >
                    {resource.type === "file" ? <Download size={16} /> : <ExternalLink size={16} />}
                  </a>
                </div>
              ))}
            </div>
            {!taskResources.length ? (
              <p className="resources-empty">No additional links or files were added for this task.</p>
            ) : null}
          </div>
        </div>

        <form className="submission-section paper-card mt-4" onSubmit={handleSubmit}>
          <h4 className="section-title mb-4">
            <FileText size={20} className="me-2 text-blue" />
            Your Submission
          </h4>

          {selectedSubmission ? (
            <div className={`current-submission-card${selectedTaskIsLocked ? " is-locked" : ""}`}>
              <div className="current-submission-head">
                <strong>Latest Submission</strong>
                <span>{new Date(selectedSubmission?.submittedAt || Date.now()).toLocaleString()}</span>
              </div>
              <div className="current-submission-items">
                {selectedSubmissionFileUrl ? (
                  <a href={selectedSubmissionFileUrl} target="_blank" rel="noreferrer">
                    File: {selectedSubmissionFileName}
                  </a>
                ) : null}
                {selectedSubmissionLink ? (
                  <a href={selectedSubmissionLink} target="_blank" rel="noreferrer">
                    Link: {selectedSubmissionLink}
                  </a>
                ) : null}
                <p>{selectedSubmissionComment ? `Comment: ${selectedSubmissionComment}` : "No comment added."}</p>
              </div>
              <small>
                {selectedTaskIsLocked
                  ? "Resubmission is not allowed for this task. You can only view your latest submission."
                  : "You can update your file/link/comment and submit again."}
              </small>
            </div>
          ) : null}

          {isFileOnly || isFileOrLink ? (
            <div className="upload-dropzone mb-3">
              <div className="upload-icon-circle">
                <CloudUpload size={24} className="text-blue" />
              </div>
              <h5 className="upload-title">Upload file</h5>
              <p className="upload-subtitle text-muted">Choose your assignment file</p>
              <input disabled={loading || selectedTaskIsLocked} type="file" className="form-control mt-3" onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </div>
          ) : null}

          {isLinkOnly || isFileOrLink ? (
            <div className="mb-3">
              <label className="form-label fw-bold d-flex align-items-center gap-2">
                <LinkIcon size={16} />
                Submission Link
              </label>
              <input
                type="url"
                className="form-control"
                value={link}
                onChange={(e) => setLink(e.target.value)}
                placeholder="https://..."
                disabled={loading || selectedTaskIsLocked}
              />
            </div>
          ) : null}

          <div className="comments-area mt-4">
            <label className="form-label fw-bold text-dark">Comments (Optional)</label>
            <textarea
              className="form-control custom-textarea"
              rows="3"
              placeholder="Add any notes for your instructor here ..."
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              disabled={loading || selectedTaskIsLocked}
            ></textarea>
          </div>

          {error ? <div className="alert alert-danger py-2 mt-3">{error}</div> : null}
          {message ? <div className="alert alert-success py-2 mt-3">{message}</div> : null}

          <div className="submit-action mt-4 text-end">
            <button disabled={loading || !selectedTaskId || selectedTaskIsLocked} type="submit" className="btn btn-primary-custom btn-lg submit-assignment-btn text-white">
              {loading ? "Submitting..." : "Submit Assignment"} <ArrowRight size={18} className="ms-2" />
            </button>
          </div>
        </form>
      </div>

      <SubmissionModal show={showModal} onHide={() => setShowModal(false)} submission={lastSubmissionResult} />
    </div>
  );
};

export default AssignmentSubmission;
